import * as PIXI from 'pixi.js-legacy'
import DesktopUIScreen from '../../../framework/src/ui/desktop/DesktopUIScreen'
import AbstractUIScreen, {
	STAGE_MODES,
} from '../../../framework/src/ui/screen/AbstractUIScreen'
import { LevelInfo } from './ajax/LevelInfo'
import { textStyle } from './constants/constants'
import { JokerBonusHelpers } from './helpers'
import { IColorScheme } from './interfaces/IColorScheme'
import { JokerWinCategory } from './JokerWinCategory'
import {
	TranslateKeys,
	TranslateService,
	TTranslateKeys,
} from './TranslateService'

export class BonusInfoTable extends PIXI.Container {
	protected colorScheme: IColorScheme
	protected borderWidth = 2
	bg: PIXI.Sprite
	bgWidth: number
	isMobile: boolean
	bigOffset: number
	smallOffset: number
	constructor(
		jackpot: JokerWinCategory,
		protected screen: AbstractUIScreen,
		protected connect: LevelInfo,
		protected appScales: [number, number],
		protected translateService: TranslateService,
		protected readonly decimalPlaces: number
	) {
		super()
		this.isMobile = !(this.screen instanceof DesktopUIScreen)
		this.colorScheme = jackpot.colorScheme
		this.borderWidth = jackpot.borderWidth
		this.bigOffset = this.isMobile ? 15 : 26
		this.smallOffset = 8
		this.bgWidth = this.isMobile
			? (this.screen.getScreenData().mode === STAGE_MODES.PORT
					? window.innerWidth - 10
					: window.innerHeight - 10) / appScales[1]
			: ((jackpot.children[0] as PIXI.Sprite).width *
					//we need to get unscaled width of jackpot, multiply it with its stage scale to get "displayed width" and then divide with tables stage scale to achieve same displayed width of both.
					jackpot.scale.x *
					appScales[0]) /
			  appScales[1]
		//#region text styles
		const primaryTextStyle: Partial<PIXI.ITextStyle> = {
			...textStyle,
			fontSize: (this.isMobile ? 16 : 10) / appScales[1],
			fill: this.colorScheme.textColors,
			stroke: this.colorScheme.textStroke,
			letterSpacing: 0.75,
			fillGradientStops:
				this.colorScheme.textColorStops.map((stop) =>
					parseFloat((stop / 100).toFixed(4))
				) ?? textStyle.fillGradientStops,
		}
		const secondaryTextStyle: Partial<PIXI.ITextStyle> = {
			...primaryTextStyle,
			fill: 0xffffff,
			fillGradientStops: [],
			stroke: null,
		}
		const AudiowideTextStyle: Partial<PIXI.ITextStyle> = {
			fontFamily: 'Audiowide-Regular',
		}
		const UbuntuMonoTextStyle: Partial<PIXI.ITextStyle> = {
			fontFamily: 'UbuntuMono-Regular',
		}
		const UbuntuRegularTextStyle: Partial<PIXI.ITextStyle> = {
			fontFamily: 'Ubuntu-Regular',
		}
		const leftAlignTextStyle: Partial<PIXI.ITextStyle> = {
			align: 'left',
		}
		const rightAlignTextStyle: Partial<PIXI.ITextStyle> = {
			align: 'right',
		}
		const centerAlignTextStyle: Partial<PIXI.ITextStyle> = {
			align: 'center',
		}
		const labelTextStyle: Partial<PIXI.ITextStyle> = {
			...secondaryTextStyle,
			...UbuntuRegularTextStyle,
			...centerAlignTextStyle,
		}
		const valueTextStyle: Partial<PIXI.ITextStyle> = {
			...primaryTextStyle,
			...UbuntuMonoTextStyle,
			...centerAlignTextStyle,
			fontSize: (this.isMobile ? 32 : 26) / appScales[1],
		}
		//#endregion
		const longestJackpotName = jackpot.parent.categoryNames.reduce(
			(prev, next) => (prev.length > next.length ? prev : next),
			''
		)
		const levelBonusContainer = new PIXI.Container()
		levelBonusContainer.y = this.smallOffset
		const levelText = new PIXI.Text(longestJackpotName.toUpperCase(), {
			...primaryTextStyle,
			...AudiowideTextStyle,
			...leftAlignTextStyle,
			fontSize: (this.isMobile ? 32 : 26) / appScales[1],
		})

		const bonusText = new PIXI.Text(
			` ${
				this.colorScheme.bonusName?.length > 0
					? this.colorScheme.bonusName.toUpperCase()
					: 'BONUS'
			}`,
			{
				...secondaryTextStyle,
				...AudiowideTextStyle,
				...leftAlignTextStyle,
				fontSize: (this.isMobile ? 32 : 26) / appScales[1],
			}
		)
		bonusText.position.set(levelText.width, 0)
		levelBonusContainer.addChild(levelText, bonusText)

		const highestWinLabel = this.createText(
			this.getText(TranslateKeys.highestWin),
			labelTextStyle
		)
		const highestWinData = this.createText('O', valueTextStyle)

		const winCountLabel = this.createText(
			this.getText(TranslateKeys.winCount),
			labelTextStyle
		)
		const winCountData = this.createText('O', valueTextStyle)

		const lastWinLabel = this.createText(
			this.getText(TranslateKeys.lastWin),
			labelTextStyle
		)
		const lastWinData = this.createText('O', valueTextStyle)

		const texture = JokerBonusHelpers.linearGradientTexture(
			(this.bgWidth - this.borderWidth / appScales[1]) * 0.95,
			2 / appScales[1],
			90,
			this.colorScheme.borderColors,
			this.colorScheme.borderColorStops
		)
		//#region text scaling to fit bg width
		this.scaleChildToWidth(levelBonusContainer)
		levelText.text = jackpot.categoryName.toUpperCase()
		bonusText.position.set(levelText.width, 0)
		lastWinData.text = '777 777 777.OO EUR'
		this.scaleChildToWidth(lastWinData)
		lastWinData.text = 'O'

		highestWinLabel.scale.set(lastWinData.scale.y)
		winCountLabel.scale.set(lastWinData.scale.y)
		lastWinLabel.scale.set(lastWinData.scale.y)

		winCountData.scale.set(lastWinData.scale.y)
		highestWinData.scale.set(lastWinData.scale.y)
		lastWinData.scale.set(lastWinData.scale.y)

		this.positionText(highestWinLabel, levelBonusContainer, this.bigOffset)
		this.positionText(highestWinData, highestWinLabel, this.smallOffset)
		this.positionText(winCountLabel, highestWinData, this.bigOffset)
		this.positionText(winCountData, winCountLabel, this.smallOffset)
		this.positionText(lastWinLabel, winCountData, this.bigOffset)
		this.positionText(lastWinData, lastWinLabel, this.smallOffset)
		//#endregion

		const references = [
			levelBonusContainer,
			highestWinLabel,
			highestWinData,
			winCountLabel,
			winCountData,
			lastWinLabel,
		]
		const lines = new Array(3).fill(0).map((_, i) => {
			const line = PIXI.Sprite.from(texture)
			line.anchor.set(0.5, 0.5)
			line.position.set(
				(this.bgWidth - this.borderWidth / appScales[1]) / 2,
				(references[i * 2].y +
					references[i * 2].height +
					references[i * 2 + 1].y) /
					2
			)
			return line
		})

		this.connect.groupId = jackpot.groupId
		this.connect.level = jackpot.categoryName
		this.connect.fetch().then((data) => {
			highestWinData.text = [
				JokerBonusHelpers.formatNumber(
					(data[0]?.highest?.display_value ?? 0).toFixed(this.decimalPlaces)
				),
				data[0]?.highest?.currency,
			]
				.filter((val) => !!val)
				.join(' ')
			winCountData.text = `${JokerBonusHelpers.formatNumber(
				data[0]?.win_counter.toFixed(0) ?? '0'
			)}`
			lastWinData.text = [
				JokerBonusHelpers.formatNumber(
					(data[0]?.last?.display_value ?? 0).toFixed(this.decimalPlaces)
				),
				data[0]?.last?.currency,
			]
				.filter((val) => !!val)
				.join(' ')
			// const longestText = [highestWinData.text,winCountData.text,lastWinData.text].sort((a,b)=>b.length-a.length)[0]
		})
		//#region background
		this.bg = PIXI.Sprite.from(
			JokerBonusHelpers.linearGradientTexture(
				this.bgWidth,
				levelBonusContainer.height * levelBonusContainer.scale.y +
					highestWinLabel.height * highestWinLabel.scale.y +
					highestWinData.height * highestWinData.scale.y +
					lastWinLabel.height * lastWinLabel.scale.y +
					lastWinData.height * lastWinData.scale.y +
					winCountLabel.height * winCountLabel.scale.y +
					winCountData.height * winCountData.scale.y +
					2 * (this.borderWidth / appScales[1]) +
					3 * (this.bigOffset / appScales[1]) +
					5 * (this.smallOffset / appScales[1]),
				90,
				this.colorScheme.borderColors,
				this.colorScheme.borderColorStops
			)
		)
		const bg2 = PIXI.Sprite.from(
			JokerBonusHelpers.linearGradientTexture(
				this.bg.width - this.borderWidth / appScales[1],
				this.bg.height - this.borderWidth / appScales[1],
				90,
				this.colorScheme.backgroundColors,
				this.colorScheme.backgroundColorStops
			)
		)
		bg2.x = this.borderWidth / appScales[1] / 2
		bg2.y = this.borderWidth / appScales[1] / 2
		this.bg.addChild(bg2)
		this.addChild(this.bg)
		//#endregion
		levelBonusContainer.x = bg2.width / 2 - levelBonusContainer.width / 2
		bg2.addChild(levelBonusContainer)
		bg2.addChild(winCountLabel, highestWinLabel, lastWinLabel)
		bg2.addChild(winCountData, highestWinData, lastWinData)
		bg2.addChild(...lines)
	}

	protected scaleChildToWidth(child: PIXI.Container) {
		child.scale.set(
			Math.min(
				child.width,
				(this.bgWidth - this.borderWidth / this.appScales[1]) * 0.95
			) / child.width
		)
	}

	protected getText(key: TTranslateKeys): string {
		return this.translateService.translate(key).toUpperCase()
	}

	protected createText(text: string, textStyle: Partial<PIXI.ITextStyle>) {
		const result = new PIXI.Text(text, {
			...textStyle,
		})
		result.anchor.set(0.5, 0)
		return result
	}

	protected positionText(
		text: PIXI.Container,
		ref: PIXI.Container,
		offset: number
	) {
		text.position.set(
			this.bgWidth / 2,
			ref.y + ref.height * ref.scale.y + offset / this.appScales[1]
		)
	}
}
